import { ReduceStore } from "flux/utils";
import Dispatcher from "Dispatcher";
import dotProp from "dot-prop-immutable";
import DefineActions from "jsmodel/DefineActions";
import humanizedKeyList from "data/util/humanizedKeyList";

const makeIndex = keyProp => fields =>
    Object.keys(fields).reduce((a, v) => {
        a[fields[v][keyProp]] = v;
        return a;
    }, {});

/*
 * This stuff runs _after_ OccurrenceRecord's custom extends method! That will
 * need to be folded into this (and entityMetaStore) when it's finally replaced
 * with pure data.
 */
const loadEntityIntoState = (state, def) => {
    const fields = {};
    for (const key of Object.keys(def.FIELD_INFO)) {
        const f = def.FIELD_INFO[key];
        fields[key] = {
            ...f,
            entityKey: def.KEY,
        };
    }

    return dotProp.set(state, [def.KEY], {
        byKey: fields,
        idxLogicalColumn: makeIndex("logicalColumn")(fields),
        idxLogicalKey: makeIndex("logicalKey")(fields),
    });
};

const LAST = 1000000;
export const getFieldInfoComparator = (infosByKey, keyProp = "key") =>
    (a, b) => {
        if (a == null) return b == null ? 0 : -1;
        if (b == null) return 1;
        const akey = a.hasOwnProperty(keyProp) ? a[keyProp] : a.get(keyProp);
        const bkey = b.hasOwnProperty(keyProp) ? b[keyProp] : b.get(keyProp);
        const aorder = infosByKey.hasOwnProperty(akey) ? infosByKey[akey].order : LAST;
        const border = infosByKey.hasOwnProperty(bkey) ? infosByKey[bkey].order : LAST;
        if (aorder < 0) return b < 0 ? 0 : -1;
        if (border < 0) return 1;
        return aorder - border;
    };

export class EntityFieldMetaStore extends ReduceStore {

    getInitialState() {
        return {};
    }

    reduce(state, action) {
        if (action.type === DefineActions._UNLOAD) {
            return this.getInitialState();
        }
        if (action.type === DefineActions.ENTITY) {
            return loadEntityIntoState(state, action.def);
        }
        return state;
    }

    _getAllInfos(entityKey) {
        const s = this.getState();
        if (! s.hasOwnProperty(entityKey)) {
            throw new Error(`No '${entityKey}' entity is known: ${Object.keys(s)}`);
        }
        return s[entityKey];
    }

    getInfo(entityKey, fieldKey) {
        const fields = this._getAllInfos(entityKey).byKey;
        if (!fields.hasOwnProperty(fieldKey)) {
            throw new Error(process.env.NODE_ENV === "development"
                ? `No '${fieldKey}' field is known on '${entityKey}': ${humanizedKeyList(fields)}`
                 :`No '${fieldKey}' field is known on '${entityKey}'`
            );
        }
        return fields[fieldKey];
    }

    getInfosByKey(entityKey) {
        return this._getAllInfos(entityKey).byKey;
    }

    getAllInfos(entityKey, optionalFilter) {
        const result = [];
        const fields = this._getAllInfos(entityKey).byKey;
        if (optionalFilter == null) {
            for (const k of Object.keys(fields)) {
                result.push(fields[k]);
            }
        } else {
            for (const k of Object.keys(fields)) {
                const info = fields[k];
                if (optionalFilter(info)) {
                    result.push(info);
                }
            }
        }
        return result;
    }

    getInfosForColumn(entityKeys, columnName) {
        const result = [];
        for (const ek of entityKeys) {
            const info = this.getInfoForColumn(ek, columnName, false);
            if (info == null) continue;
            result.push(info);
        }
        return result;
    }

    getInfosForColumnKey(entityKeys, colKey) {
        const result = [];
        for (const ek of entityKeys) {
            const info = this.getInfoForColumnKey(ek, colKey, false);
            if (info == null) continue;
            result.push(info);
        }
        return result;
    }

    _getInfoFromIndex(entityKey, indexName, value, errorOnMissing = true) {
        const indexes = this._getAllInfos(entityKey);
        if (indexes[indexName].hasOwnProperty(value)) {
            return indexes.byKey[indexes[indexName][value]];
        }
        if (! errorOnMissing) return null;
        throw new Error(`Entity '${entityKey}' has no field for ${indexName.substr(3)} '${value}'`);
    }

    getInfoForColumn(entityKey, colName, errorOnMissing = true) {
        return this._getInfoFromIndex(entityKey, "idxLogicalColumn", colName, errorOnMissing);
    }

    getInfoForColumnKey(entityKey, colKey, errorOnMissing = true) {
        return this._getInfoFromIndex(entityKey, "idxLogicalKey", colKey, errorOnMissing);
    }

}

const entityFieldMetaStore = new EntityFieldMetaStore(Dispatcher);

export default entityFieldMetaStore;



// WEBPACK FOOTER //
// ./app/data/meta/entityFieldMetaStore.js